import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ArrowRight, MapPin, HardHat } from "lucide-react";
import UnreadBadge from "@/components/UnreadBadge";
import MembershipCard from "@/components/MembershipCard";

export default function ContractorDashboard() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/claims")
      .then((r) => setLeads(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="contractor-dashboard">
      <div className="mb-10">
        <p className="text-xs uppercase tracking-widest text-[#C25934] font-medium mb-2">
          Contractor portal
        </p>
        <h1 className="font-['Outfit'] text-4xl font-semibold tracking-tight">
          Leads in zip {user?.zip_code || "—"}
        </h1>
        <p className="text-gray-600 mt-2">
          Pre-qualified claims matched to your service area.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-10">
        <StatCard label="Total leads" value={leads.length} />
        <StatCard
          label="Active claims"
          value={leads.filter((l) => l.status !== "closed").length}
        />
        <StatCard
          label="Service zip"
          value={user?.zip_code || "Not set"}
          icon={MapPin}
        />
      </div>

      <div className="mb-10">
        <MembershipCard />
      </div>

      <Card className="border-[#E5E7EB]">
        <CardHeader>
          <CardTitle className="font-['Outfit']">Lead board</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-gray-500 py-8 text-center">Loading…</p>
          ) : leads.length === 0 ? (
            <div className="text-center py-12">
              <HardHat className="w-10 h-10 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No leads in your zip yet.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date of loss</TableHead>
                  <TableHead>Damage</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Carrier</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((c) => (
                  <TableRow key={c.id} data-testid={`lead-row-${c.id}`}>
                    <TableCell>{c.date_of_loss}</TableCell>
                    <TableCell className="capitalize">
                      <div className="flex items-center gap-2">
                        {c.damage_type}
                        <UnreadBadge count={c.unread_count} claimId={c.id} />
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {c.address}
                    </TableCell>
                    <TableCell>{c.carrier}</TableCell>
                    <TableCell className="capitalize">
                      {c.status?.replace("_", " ")}
                    </TableCell>
                    <TableCell className="text-right">
                      <Link
                        to={`/claim/${c.id}`}
                        data-testid={`open-lead-${c.id}`}
                      >
                        <Button variant="ghost" size="sm">
                          View <ArrowRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <Card className="border-[#E5E7EB]">
      <CardContent className="p-6">
        <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">
          {label}
        </p>
        <div className="flex items-end justify-between">
          <p className="font-['Outfit'] text-3xl font-semibold text-[#1E3F33]">
            {value}
          </p>
          {Icon && <Icon className="w-5 h-5 text-[#C25934]" />}
        </div>
      </CardContent>
    </Card>
  );
}
